import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { searchIreferGuidelines } from '../../services/ireferService';

const GuidelinesReference = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBodyRegion, setSelectedBodyRegion] = useState('all');
  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const categoryOptions = [
    { value: 'all', label: t('guidelines.allCategories') },
    { value: 'Breast', label: 'Breast' },
    { value: 'Cancer', label: 'Cancer' },
    { value: 'Cardiovascular', label: 'Cardiovascular' },
    { value: 'Gastrointestinal', label: 'Gastrointestinal' },
    { value: 'Genitourinary', label: 'Genitourinary' },
    { value: 'Musculoskeletal', label: 'Musculoskeletal' },
    { value: 'Neurological', label: 'Neurological' },
    { value: 'Pediatric', label: 'Pediatric' },
    { value: 'Respiratory', label: 'Respiratory' },
    { value: 'Trauma', label: 'Trauma' }
  ];

  const bodyRegionOptions = [
    { value: 'all', label: t('guidelines.allBodyRegions') },
    { value: 'Brain', label: 'Brain' },
    { value: 'Head', label: 'Head' },
    { value: 'Neck', label: 'Neck' },
    { value: 'Chest', label: 'Chest' },
    { value: 'Abdomen', label: 'Abdomen' },
    { value: 'Pelvis', label: 'Pelvis' },
    { value: 'Spine', label: 'Spine' },
    { value: 'Shoulder', label: 'Shoulder' },
    { value: 'Elbow', label: 'Elbow' },
    { value: 'Wrist', label: 'Wrist' },
    { value: 'Hand', label: 'Hand' },
    { value: 'Hip', label: 'Hip' },
    { value: 'Knee', label: 'Knee' },
    { value: 'Ankle', label: 'Ankle' },
    { value: 'Foot', label: 'Foot' },
    { value: 'Extremities', label: 'Extremities' },
    { value: 'Multiple', label: 'Multiple' }
  ];

  // Fetch guidelines from database
  useEffect(() => {
    fetchGuidelines();
  }, [selectedCategory, selectedBodyRegion]);

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedCategory !== 'all') filters.category = selectedCategory;
      if (selectedBodyRegion !== 'all') filters.bodyRegion = selectedBodyRegion;
      
      const data = await searchIreferGuidelines(filters);
      setGuidelines(data || []);
    } catch (error) {
      console.error('Error fetching guidelines:', error);
      setGuidelines([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredGuidelines = guidelines?.filter(guideline => {
    if (!searchQuery) return true;
    const query = searchQuery?.toLowerCase();
    return (
      guideline?.title?.toLowerCase()?.includes(query) ||
      guideline?.clinicalScenario?.toLowerCase()?.includes(query) ||
      guideline?.ireferCode?.toLowerCase()?.includes(query) ||
      guideline?.keywords?.some(keyword => keyword?.toLowerCase()?.includes(query))
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil((filteredGuidelines?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGuidelines = filteredGuidelines?.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedBodyRegion]);

  const handleGuidelineClick = (guideline) => {
    setSelectedGuideline(guideline);
    setShowDetailModal(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Breast': 'bg-pink-100 text-pink-700',
      'Cancer': 'bg-purple-100 text-purple-700',
      'Cardiovascular': 'bg-red-100 text-red-700',
      'Gastrointestinal': 'bg-orange-100 text-orange-700',
      'Genitourinary': 'bg-blue-100 text-blue-700',
      'Musculoskeletal': 'bg-green-100 text-green-700',
      'Neurological': 'bg-indigo-100 text-indigo-700',
      'Pediatric': 'bg-yellow-100 text-yellow-700',
      'Respiratory': 'bg-cyan-100 text-cyan-700',
      'Trauma': 'bg-rose-100 text-rose-700'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation 
        userRole={profile?.role || 'clinician'} 
        creditBalance={profile?.credits_remaining || 0}
        subscriptionTier={profile?.subscription_tier || 'Free'} 
      />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t('guidelines.title')} - iRefer Library
                </h1>
                <p className="text-muted-foreground">
                  {filteredGuidelines?.length || 0} comprehensive clinical imaging guidelines
                </p>
              </div>
              <Button 
                variant="outline" 
                iconName="ArrowLeft"
                onClick={() => navigate('/dashboard')}
              >
                {t('guidelines.backToDashboard')}
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="clinical-card p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <Input
                  placeholder="Search by title, code, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  iconName="Search"
                />
              </div>
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e?.target?.value)}
                placeholder="Filter by Category"
              />
              <Select
                options={bodyRegionOptions}
                value={selectedBodyRegion}
                onChange={(e) => setSelectedBodyRegion(e?.target?.value)}
                placeholder="Filter by Body Region"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Guidelines Grid */}
          {!loading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentGuidelines?.length > 0 ? (
                  currentGuidelines?.map(guideline => (
                    <div 
                      key={guideline?.ireferCode}
                      className="clinical-card p-6 cursor-pointer hover:shadow-lg transition-all hover:border-primary"
                      onClick={() => handleGuidelineClick(guideline)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-mono px-2 py-1 bg-primary text-white rounded">
                              {guideline?.ireferCode}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(guideline?.category)}`}>
                              {guideline?.category}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">
                            {guideline?.title}
                          </h3>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`
                            inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
                            ${guideline?.evidenceGrade === 'A' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'}
                          `}>
                            {guideline?.evidenceGrade}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {guideline?.clinicalScenario}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">{guideline?.bodyRegion}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <Icon name="Activity" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">{guideline?.modality}</span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
                        <span className="flex items-center gap-1">
                          <Icon name="ExternalLink" size={14} />
                          View Details
                        </span>
                        <Icon name="ChevronRight" size={16} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full flex flex-col items-center justify-center py-12">
                    <Icon name="FileX" size={48} color="var(--color-muted-foreground)" />
                    <p className="text-muted-foreground mt-3">No matching guidelines found</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                        setSelectedBodyRegion('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {filteredGuidelines?.length > itemsPerPage && (
                <div className="clinical-card p-6 mt-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="text-sm text-muted-foreground">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredGuidelines?.length)} of {filteredGuidelines?.length} guidelines
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        iconName="ChevronLeft"
                      >
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`
                                px-3 py-1 rounded text-sm font-medium transition-colors
                                ${currentPage === pageNum 
                                  ? 'bg-primary text-white' :'bg-muted text-foreground hover:bg-primary/10'
                                }
                              `}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <span className="px-2 text-muted-foreground">...</span>
                            <button
                              onClick={() => setCurrentPage(totalPages)}
                              className="px-3 py-1 rounded text-sm font-medium bg-muted text-foreground hover:bg-primary/10"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        iconName="ChevronRight"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Info Card */}
          <div className="clinical-card p-6 mt-8 bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">About iRefer Guidelines</h4>
                <p className="text-sm text-muted-foreground">
                  These evidence-based imaging guidelines are sourced from iRefer.org.uk, the Royal College of Radiologists' 
                  clinical decision support tool. All guidelines include evidence grades (A = strong evidence, B = moderate evidence) 
                  and recommended imaging modalities based on clinical scenarios.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guideline Detail Modal */}
      {showDetailModal && selectedGuideline && (
        <GuidelineDetailModal
          guideline={selectedGuideline}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedGuideline(null);
          }}
        />
      )}
    </div>
  );
};

// Guideline Detail Modal Component
const GuidelineDetailModal = ({ guideline, onClose }) => {
  const getCategoryColor = (category) => {
    const colors = {
      'Breast': 'bg-pink-100 text-pink-700 border-pink-300',
      'Cancer': 'bg-purple-100 text-purple-700 border-purple-300',
      'Cardiovascular': 'bg-red-100 text-red-700 border-red-300',
      'Gastrointestinal': 'bg-orange-100 text-orange-700 border-orange-300',
      'Genitourinary': 'bg-blue-100 text-blue-700 border-blue-300',
      'Musculoskeletal': 'bg-green-100 text-green-700 border-green-300',
      'Neurological': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'Pediatric': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Respiratory': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'Trauma': 'bg-rose-100 text-rose-700 border-rose-300'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-mono px-3 py-1 bg-primary text-white rounded">
                {guideline?.ireferCode}
              </span>
              <span className={`text-sm px-3 py-1 rounded border ${getCategoryColor(guideline?.category)}`}>
                {guideline?.category}
              </span>
              <span className={`
                inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold
                ${guideline?.evidenceGrade === 'A' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'}
              `}>
                Evidence Grade: {guideline?.evidenceGrade}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-foreground">{guideline?.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 hover:bg-muted rounded-lg transition-colors ml-4"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Clinical Scenario */}
          <div className="clinical-card p-5 bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-3">
              <Icon name="Stethoscope" size={20} color="var(--color-primary)" className="mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Clinical Scenario</h3>
                <p className="text-sm text-muted-foreground">{guideline?.clinicalScenario}</p>
              </div>
            </div>
          </div>

          {/* Imaging Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="clinical-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="MapPin" size={18} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground">Body Region</h3>
              </div>
              <p className="text-sm text-muted-foreground">{guideline?.bodyRegion}</p>
            </div>

            <div className="clinical-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Activity" size={18} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground">Recommended Modality</h3>
              </div>
              <p className="text-sm text-muted-foreground font-medium">{guideline?.modality}</p>
            </div>
          </div>

          {/* Radiation Dose Information */}
          <div className="clinical-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Zap" size={18} color="var(--color-primary)" />
              <h3 className="font-semibold text-foreground">Radiation Exposure</h3>
            </div>
            {guideline?.modality?.toLowerCase()?.includes('mammography') || 
             guideline?.modality?.toLowerCase()?.includes('ct') || 
             guideline?.modality?.toLowerCase()?.includes('x-ray') || 
             guideline?.modality?.toLowerCase()?.includes('barium') ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">☢</span>
                  <span className="text-sm font-medium text-orange-700">Ionising Radiation</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {guideline?.modality?.toLowerCase()?.includes('ct') || guideline?.modality?.toLowerCase()?.includes('barium') 
                    ? 'Moderate radiation dose - benefits should outweigh risks. Alternative non-ionising modalities should be considered when appropriate.' :'Low radiation dose - generally safe when clinically indicated. Follow ALARA (As Low As Reasonably Achievable) principles.'}
                </p>
              </div>
            ) : guideline?.modality?.toLowerCase()?.includes('ultrasound') || 
                    guideline?.modality?.toLowerCase()?.includes('mri') ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                  <span className="text-sm font-medium text-success">No Ionising Radiation</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {guideline?.modality?.toLowerCase()?.includes('ultrasound')
                    ? 'Ultrasound uses sound waves - completely safe with no radiation exposure. Ideal for pregnant patients and repeated examinations.' :'MRI uses magnetic fields - no radiation exposure. Contraindicated in patients with certain metallic implants or devices.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={18} color="var(--color-muted-foreground)" />
                  <span className="text-sm font-medium text-muted-foreground">Radiation Information Not Available</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Please refer to the full iRefer guideline for detailed radiation exposure information.
                </p>
              </div>
            )}
          </div>

          {/* Keywords */}
          {guideline?.keywords && guideline?.keywords?.length > 0 && (
            <div className="clinical-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Tag" size={18} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground">Clinical Keywords</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {guideline?.keywords?.map((keyword, index) => (
                  <span 
                    key={index}
                    className="text-xs px-3 py-1 bg-muted text-foreground rounded-full border border-border"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Evidence Grade Info */}
          <div className="clinical-card p-5 bg-green-50 border border-green-200">
            <div className="flex items-start gap-3">
              <Icon name="Award" size={20} color="var(--color-success)" className="mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Evidence Grade: {guideline?.evidenceGrade}</h3>
                <p className="text-sm text-muted-foreground">
                  {guideline?.evidenceGrade === 'A' ?'Strong evidence supporting this imaging recommendation based on high-quality clinical studies and expert consensus.' :'Moderate evidence supporting this imaging recommendation based on clinical experience and available studies.'}
                </p>
              </div>
            </div>
          </div>

          {/* iRefer Link */}
          <div className="clinical-card p-5 bg-primary/10 border border-primary/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="ExternalLink" size={20} color="var(--color-primary)" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">View Full Guideline on iRefer</h3>
                  <p className="text-xs text-muted-foreground">Access complete clinical details and references</p>
                </div>
              </div>
              <Button
                onClick={() => window.open(guideline?.ireferUrl, '_blank')}
                iconName="ExternalLink"
              >
                Open iRefer
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background border-t border-border p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => window.open(guideline?.ireferUrl, '_blank')}
            iconName="ExternalLink"
          >
            View on iRefer.org.uk
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuidelinesReference;